interface ChannelIOBootSettings {
  pluginKey: string;
  memberId?: string;
  profile?: {
    name?: string;
    mobileNumber?: string;
    merchant?: string;
    phoneNum?: string;
    [key: string]: string | undefined;
  };
  [key: string]: unknown;
}

interface ChannelIOFunction {
  (command: 'boot', settings: ChannelIOBootSettings): void;
  (command: 'shutdown'): void;
  (command: string, ...args: unknown[]): void;
  q: unknown[][];
  c: (args: unknown[]) => void;
}

declare global {
  interface Window {
    ChannelIO?: ChannelIOFunction;
    ChannelIOInitialized?: boolean;
  }
}

class ChannelService {
  private static instance: ChannelService | null = null;

  private isMessengerOpen = false;

  private constructor() {
    if (typeof window !== 'undefined') {
      this.loadScript();
    }
  }

  static getInstance(): ChannelService {
    if (!ChannelService.instance) {
      ChannelService.instance = new ChannelService();
    }
    return ChannelService.instance;
  }

  private loadScript(): void {
    if (window.ChannelIO) {
      (window.console.error || window.console.log || (() => {}))('ChannelIO script included twice.');
      return;
    }

    const ch = function (...args: unknown[]) {
      ch.c(args);
    } as ChannelIOFunction;

    ch.q = [];
    ch.c = function (args: unknown[]) {
      ch.q.push(args);
    };

    window.ChannelIO = ch;

    const loadChannelScript = (): void => {
      if (window.ChannelIOInitialized) {
        return;
      }
      window.ChannelIOInitialized = true;

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
      script.charset = 'UTF-8';

      const firstScript = document.getElementsByTagName('script')[0];
      firstScript?.parentNode?.insertBefore(script, firstScript);
    };

    if (document.readyState === 'complete') {
      loadChannelScript();
    } else {
      window.addEventListener('DOMContentLoaded', loadChannelScript, false);
      window.addEventListener('load', loadChannelScript, false);
    }
  }

  boot(settings: ChannelIOBootSettings): void {
    if (process.env.NODE_ENV !== 'production') {
      return;
    }
    if (typeof window !== 'undefined' && window.ChannelIO) {
      window.ChannelIO('boot', {
        ...settings,
        hideChannelButtonOnBoot: true,
      });
      window.ChannelIO('hideChannelButton');
      window.ChannelIO('onShowMessenger', () => {
        this.isMessengerOpen = true;
      });
      window.ChannelIO('onHideMessenger', () => {
        this.isMessengerOpen = false;
      });
    }
  }

  shutdown(): void {
    if (typeof window !== 'undefined' && window.ChannelIO) {
      window.ChannelIO('shutdown');
    }
    this.isMessengerOpen = false;
  }

  showMessenger(): void {
    if (typeof window !== 'undefined' && window.ChannelIO) {
      window.ChannelIO('showMessenger');
    }
    this.isMessengerOpen = true;
  }

  hideMessenger(): void {
    if (typeof window !== 'undefined' && window.ChannelIO) {
      window.ChannelIO('hideMessenger');
    }
    this.isMessengerOpen = false;
  }

  toggleMessenger(): void {
    if (this.isMessengerOpen) {
      this.hideMessenger();
      return;
    }
    this.showMessenger();
  }

  hideChannelButton(): void {
    if (typeof window !== 'undefined' && window.ChannelIO) {
      window.ChannelIO('hideChannelButton');
    }
  }
}

export default ChannelService.getInstance();
